import { deriveAvatarColor, deriveInitials } from "@/lib/colors";
import type { AvatarUser } from "@/components/ui/Avatar";
import type { BoardPerson, MentionItem, UUID, WorkspaceMember } from "@/lib/types";

/**
 * Every face a BOARD may need to draw, keyed by user id.
 *
 * 🔴 THE POOL IS `GET /boards/{id}/people` (members ∪ guests), NEVER the workspace members
 * alone. A person cell can legitimately hold someone who is not a member of the board's
 * workspace: assigning a cross-department colleague mints a ROW-SCOPED GUEST GRANT, which is
 * the whole cross-department story (a ticket's creator is routinely exactly this). Resolve
 * them against workspace members and they come back `undefined`, and the UI then draws them as
 * an em-dash "—", a "?" or nothing at all.
 *
 * This repo has now shipped that same bug four times — the guest "white circle" on person
 * cells, the "—" recipient in ActionDialog, the Kanban card avatar, and (memo-side, its own
 * pool) a to-do assignee who missed the meeting. Each was fixed in isolation, in one file,
 * which is precisely why it kept coming back. One converter, one pool, one place to fix.
 *
 * Workspace members are layered in FIRST and board people SECOND, so a guest's richer record
 * (and their department ring) wins over a stale member row for the same id.
 */
export function boardPeopleById(
  members: WorkspaceMember[] | undefined,
  people: BoardPerson[] | undefined,
): Map<UUID, AvatarUser> {
  const byId = new Map<UUID, AvatarUser>();

  const put = (
    id: UUID,
    displayName: string | null,
    username: string | null,
    email: string | null,
    departmentWorkspaceId: UUID | null,
    departmentColor: string | null,
  ) => {
    // Fall back through name → username → email so a person with no display name is still a
    // recognisable human, not "—". Only a genuinely unknown id has no face.
    const name = displayName ?? username ?? email ?? "?";
    byId.set(id, {
      id,
      display_name: name,
      initials: deriveInitials(name),
      avatar_color: deriveAvatarColor(id),
      username,
      department_workspace_id: departmentWorkspaceId,
      department_color: departmentColor,
    });
  };

  for (const m of members ?? [])
    put(
      m.user_id,
      m.display_name,
      m.username,
      m.email,
      m.department_workspace_id ?? null,
      m.department_color ?? null,
    );

  for (const p of people ?? [])
    put(
      p.user_id,
      p.display_name,
      p.username,
      p.email,
      p.department_workspace_id ?? null,
      p.department_color ?? null,
    );

  return byId;
}

/**
 * The @-mention pool for a row's comments + description fields.
 *
 * 🔴 The pool is ROW-SCOPED, and the server decides it: GET /rows/{id}/mention-people
 * (useRowMentionPeople) returns EXACTLY the mentionable set for this row — board members
 * (home ∪ shared-into workspaces) ∪ the row's creator ∪ everyone the row is shared with
 * (Phase 4 row_shares, direct users + department members). So this is a plain projection to
 * MentionItem with NO client-side filtering: a per-ticket shared-in colleague is offered the
 * board's members/owners (and the other people on that row), and members are offered the
 * shared-in people — without exposing unrelated guests or other rows' shares. The backend
 * `access.row_mention_user_ids` and this endpoint are one definition, so the pool you can pick
 * from can't drift from the pool the comment-notification path accepts.
 *
 * (Replaces the old board-wide members-∪-creator filter, which dropped exactly the shared-in
 * colleague you most wanted to tag — see the identity-pool bug class in boardPeopleById above.)
 */
export function rowMentionItems(people: BoardPerson[] | undefined): MentionItem[] {
  return (people ?? []).map((p) => ({ id: p.user_id, label: p.display_name ?? p.email }));
}

/**
 * Compare two @-mention items alphabetically by the display name — first name first,
 * since LogicLine names are "Vorname Nachname" (user preference 2026-09-22, after a
 * first pass sorted by surname). Case/locale-insensitive (de).
 */
export function compareMentionByName(a: MentionItem, b: MentionItem): number {
  return a.label.localeCompare(b.label, "de", { sensitivity: "base" });
}
